// resolveLanding — pure landing resolver, keyed on REACHABILITY (not raw
// status), so an expired-trial project (which stays status='active') is treated
// as blocked. Per FRONTEND-PATTERNS §Resolver chain; called by RootResolver at
// `/` after useMe() resolves.
//
// "Reachable" = projectAccess(...) === 'ok' — the project's INTRINSIC state,
// viewer-independent (a blocked project is never an auto-land target, for anyone,
// incl. a sysadmin: they reach blocked projects via the platform portal, not by
// landing). Cases, in order:
//   1. last_project_id reachable                  → /p/<id>/
//   2. exactly 1 reachable                         → /p/<id>/
//   3. >1 reachable                                → /select-project
//   4. 0 reachable, sysadmin                       → /platform/projects
//      (a sysadmin is never sent to /project-blocked — they manage every project,
//       reachable or not, in the platform portal)
//   5. 0 reachable, member, exactly 1 membership   → /project-blocked?reason=<that project's access state>
//   5b. 0 reachable, member, >1 membership         → /project-blocked?reason=all-blocked
//   6. 0 memberships                               → /no-projects
//
// Pure function — `now` is passed in (callers capture it once) so it stays
// idempotent + testable. The component-level <Navigate> lives in RootResolver.

import type { MeResponse } from '@ems-app/schemas/auth/responses';
import { isReachable, projectAccess } from './projectAccess.js';

export function resolveLanding(me: MeResponse, now: number): { path: string } {
  const isSysadmin = me.user.is_sysadmin;
  const reachable = me.availableProjects.filter((p) => isReachable(p, now));
  const lastId = me.user.last_project_id;

  // (1) last_project_id reachable
  if (lastId !== null && reachable.some((p) => p.id === lastId)) {
    return { path: `/p/${lastId}/` };
  }

  // (2) exactly one reachable
  if (reachable.length === 1) {
    return { path: `/p/${reachable[0]!.id}/` };
  }

  // (3) more than one reachable
  if (reachable.length > 1) {
    return { path: '/select-project' };
  }

  // (4) nothing reachable + sysadmin → the platform portal (manage/create projects)
  if (isSysadmin) {
    return { path: '/platform/projects' };
  }

  // (5) member, has memberships but none reachable → the blocked page.
  // A single blocked project shows its specific reason; multiple → all-blocked.
  if (me.availableProjects.length === 1) {
    const only = me.availableProjects[0]!;
    const reason = projectAccess(only, now); // 'suspended' | 'terminated' | 'trial-expired'
    // Carry the project id so the blocked page names the context (the switcher).
    return { path: `/project-blocked?reason=${reason}&project=${only.id}` };
  }
  if (me.availableProjects.length > 1) {
    return { path: '/project-blocked?reason=all-blocked' };
  }

  // (6) zero memberships
  return { path: '/no-projects' };
}
